import React, { useState, useCallback, useMemo, useEffect, useRef, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ThemeContext } from '../contexts/ThemeContext';
import { APP_NAME } from '../config/frontend';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import apiService from '../services/apiService';

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};
import { 
  BarChart3, 
  Package, 
  Users, 
  FileText, 
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  CreditCard,
  DollarSign,
  Bell,
  Settings,
  Search,
  Calendar,
  ChevronDown,
  ChevronRight,
  Menu,
  Activity,
  PlusCircle,
  User,
  LogOut,
  RefreshCw,
  Filter,
  PanelLeftClose,
  PanelLeftOpen,
  Moon,
  Sun,
  ArrowUpRight, 
  ArrowDownRight,
  Clock,
  X,
  Calculator
} from 'lucide-react';
import toast from 'react-hot-toast';

const BulkSales = () => {
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useContext(ThemeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [bulkSaleId, setBulkSaleId] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    customerId: '',
    phoneNumber: '',
    email: '',
    outstandingAmount: 'Ghs 0.00',
    address: ''
  });
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isQuantityFocused, setIsQuantityFocused] = useState(false);
  const [isUomFocused, setIsUomFocused] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [workflowStep, setWorkflowStep] = useState('search'); // 'search', 'quantity', 'uom'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [currentProductQuantity, setCurrentProductQuantity] = useState(null);
  const [remark, setRemark] = useState('');
  
  // Receipt Modal State
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [currencySettings, setCurrencySettings] = useState({
    currency_symbol: '₵',
    currency_code: 'GHS'
  });
  const [companySettings, setCompanySettings] = useState({
    company_name: 'Grovalue Enterprise',
    company_address: '123 Business Street, City, Country',
    company_phone: '+233 XX XXX XXXX',
    company_email: '',
    company_website: '',
    tax_number: ''
  });

  // Bulk Reports State
  const [reportsDateRange, setReportsDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [activeReportTab, setActiveReportTab] = useState('dashboard');
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsData, setReportsData] = useState({
    dashboardStats: null,
    salesReport: null,
    productsReport: null,
    customersReport: null
  });
  
  // Bulk Sales History State
  const [bulkSalesHistory, setBulkSalesHistory] = useState([]);
  const [bulkSalesHistoryLoading, setBulkSalesHistoryLoading] = useState(false);
  const [bulkSalesHistorySearchTerm, setBulkSalesHistorySearchTerm] = useState('');
  const [bulkSalesHistoryFilter, setBulkSalesHistoryFilter] = useState('all');
  const [bulkSalesHistoryStartDate, setBulkSalesHistoryStartDate] = useState('');
  const [bulkSalesHistoryEndDate, setBulkSalesHistoryEndDate] = useState('');
  const [bulkSalesHistoryStatus, setBulkSalesHistoryStatus] = useState('all');
  
  // Action Modal States
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkSaleDeleteModal, setShowBulkSaleDeleteModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  
  // Edit Modal States
  const [editFormData, setEditFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    company_name: '',
    sales_agent: '',
    sales_date: '',
    mode_of_sales: 'Cash',
    remark: '',
    total_amount: '',
    discount: 0
  });
  const [editLoading, setEditLoading] = useState(false);
  const [bulkSalesHistoryCurrentPage, setBulkSalesHistoryCurrentPage] = useState(1);
  const [bulkSalesHistoryPerPage, setBulkSalesHistoryPerPage] = useState(20);
  const [bulkSalesHistoryTotal, setBulkSalesHistoryTotal] = useState(0);
  const [bulkSalesHistorySortBy, setBulkSalesHistorySortBy] = useState('created_at');
  const [bulkSalesHistorySortOrder, setBulkSalesHistorySortOrder] = useState('desc');

  // Calculate totals (products are VAT-inclusive)
  const totalAmount = useMemo(() => {
    return selectedProducts.reduce((sum, product) => sum + product.amount, 0);
  }, [selectedProducts]);

  const vat = useMemo(() => {
    // Extract VAT from VAT-inclusive price: VAT = Total × (15/115)
    return totalAmount * (15 / 115);
  }, [totalAmount]);

  const subtotal = useMemo(() => {
    // Subtotal is the amount before VAT was added
    return totalAmount - vat;
  }, [totalAmount, vat]);

  const grandTotal = useMemo(() => {
    // Grand total is the total amount (already includes VAT) minus any discount
    return totalAmount - discount;
  }, [totalAmount, discount]);

  // Filter customers based on search query
  const filteredCustomers = useMemo(() => {
    if (!customerSearchQuery.trim()) {
      return customers;
    }
    return customers.filter(customer => 
      (customer.name || customer.customer_name || '')
        .toLowerCase()
        .includes(customerSearchQuery.toLowerCase())
    );
  }, [customers, customerSearchQuery]);


  const fetchCustomers = useCallback(async () => {
    try {
      if (!apiService.isAuthenticated()) {
        console.error('No authentication token found');
        toast.error('Authentication required');
        return;
      }

      const data = await apiService.getCustomers('per_page=1000&status=active');
      const customersData = data.customers || [];
      setCustomers(customersData);
    } catch (error) {
      if (error.message === 'Authentication failed') {
        console.error('Authentication failed');
        toast.error('Authentication failed. Please login again.');
      } else {
        console.error('Error fetching customers:', error);
        toast.error('Error loading customers');
      }
      setCustomers([]);
    }
  }, []);

  // Handle clicking outside customer dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.querySelector('.customer-dropdown-container');
      if (dropdown && !dropdown.contains(event.target)) {
        setShowCustomerDropdown(false);
      }
    };

    if (showCustomerDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomerDropdown]);

  const handleCustomerSelect = useCallback(async (customerId) => {
    if (!customerId) {
      setSelectedCustomer(null);
      setSelectedCustomerName('');
      setCustomerSearchQuery('');
      setCustomerInfo({
        customerId: '',
        phoneNumber: '',
        email: '',
        outstandingAmount: 'Ghs 0.00',
        address: ''
      });
      return;
    }

    try {
      if (!apiService.isAuthenticated()) {
        console.error('No authentication token found');
        toast.error('Authentication required');
        return;
      }

      const response = await apiService.getCustomer(customerId);
      
      // Extract customer data from response
      const customer = response.customer || response.data || response;
      
      setSelectedCustomer(customer);
      setSelectedCustomerName(customer.name || customer.customer_name || '');
      setCustomerSearchQuery('');
      setShowCustomerDropdown(false);
      
      // Update customer info with proper field mapping
      setCustomerInfo({
        customerId: customer.customerID || customer.customer_id || customer.id || '',
        phoneNumber: customer.phone || customer.phone_number || '',
        email: customer.email || '',
        outstandingAmount: customer.balance || customer.outstanding_amount || customer.outstanding_balance || customer.debt ? `Ghs ${parseFloat(customer.balance || customer.outstanding_amount || customer.outstanding_balance || customer.debt || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'Ghs 0.00',
        address: customer.address || ''
      });
      
    } catch (error) {
      if (error.message === 'Authentication failed') {
        console.error('Authentication failed');
        toast.error('Authentication failed. Please login again.');
      } else {
        console.error('Error fetching customer details:', error);
        toast.error('Error loading customer details');
      }
    }
  }, []);

  const handleCardClick = useCallback((href) => {
    navigate(href);
  }, [navigate]);

    const generateBulkSaleId = useCallback(async () => {
    try {
      setLoading(true);
      
      if (!apiService.isAuthenticated()) {
        console.error('No authentication token found');
        toast.error('Authentication required');
        return;
      }

      // Make API call to get the last bulk sale ID from database
      const data = await apiService.getLastBulkSaleId();
      
      const lastId = data.data?.next_id || 'BULK-20250101000'; // Default fallback
      
      // Extract the numeric part and increment
      const numericPart = parseInt(lastId.slice(-3)); // Get last 3 digits
      const newNumericPart = (numericPart + 1).toString().padStart(3, '0');
      
      // Generate new ID with current date
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      
      const newId = `BULK-${year}${month}${day}${newNumericPart}`;
      setBulkSaleId(newId);
    } catch (error) {
      console.error('Error generating bulk sale ID:', error);
      // Fallback: generate ID based on current date
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const fallbackId = `BULK-${year}${month}${day}001`;
      setBulkSaleId(fallbackId);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleModalOpen = useCallback((modalType) => {
    // Check if user is authenticated
    if (!apiService.isAuthenticated()) {
      toast.error('Please login to access this feature');
      return;
    }

    setActiveModal(modalType);
    setModalOpen(true);
    
    // Generate new bulk sale ID and fetch customers when opening create bulk sale modal
    if (modalType === 'create-bulk-sale') {
      generateBulkSaleId();
      fetchCustomers();
    }
  }, [generateBulkSaleId, fetchCustomers]);

  const resetModalFields = useCallback(() => {
    setBulkSaleId('');
    setSelectedCustomer(null);
    setCustomerInfo({
      customerId: '',
      phoneNumber: '',
      email: '',
      outstandingAmount: 'Ghs 0.00',
      address: ''
    });
    setSearchQuery('');
    setSelectedProducts([]);
    setShowProductDropdown(false);
    setSelectedIndex(-1);
    setSelectedCustomerName('');
    setCustomerSearchQuery('');
    setSelectedProduct(null);
    setWorkflowStep('search');
    setDiscount(0);
    setCurrentProductQuantity(null);
    setRemark('');
    
    // Reset bulk sales history state
    setBulkSalesHistory([]);
    setBulkSalesHistoryLoading(false);
    setBulkSalesHistorySearchTerm('');
    setBulkSalesHistoryFilter('all');
    setBulkSalesHistoryStartDate('');
    setBulkSalesHistoryEndDate('');
    setBulkSalesHistoryStatus('all');
    setBulkSalesHistoryCurrentPage(1);
    setBulkSalesHistoryTotal(0);
    setBulkSalesHistorySortBy('created_at');
    setBulkSalesHistorySortOrder('desc');
  }, []);

  const handleSaveBulkSale = useCallback(async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer first!', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
      return;
    }

    if (selectedProducts.length === 0) {
      toast.error('Please add at least one product to the cart!', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
      return;
    }

    try {
      setLoading(true);
      
      const bulkSaleData = {
        customer_id: selectedCustomer.id,
        customer_name: selectedCustomer.name || selectedCustomer.customer_name,
        customer_phone: selectedCustomer.phone_number,
        customer_email: selectedCustomer.email,
        customer_address: selectedCustomer.address,
        company_name: 'Grovalue Enterprise',
        sales_agent: user?.name || user?.username || 'Agent',
        sales_date: new Date().toISOString().split('T')[0],
        mode_of_sales: 'Cash',
        remark: remark,
        items: selectedProducts.map(product => ({
          product_id: product.id,
          product_name: product.name,
          quantity: product.quantity,
          uom: product.uom,
          unit_price: product.price,
          total_amount: product.amount
        })),
        subtotal: subtotal,
        vat: vat,
        discount: discount,
        grand_total: grandTotal,
        status: 'completed'
      };

      const response = await apiService.createBulkSale(bulkSaleData);
      
      // Prepare receipt data
      const receipt = {
        bulkSaleId: response.bulk_sale_id || bulkSaleId,
        customer: {
          name: selectedCustomer.name || selectedCustomer.customer_name,
          phone: selectedCustomer.phone_number,
          email: selectedCustomer.email,
          address: selectedCustomer.address
        },
        items: selectedProducts,
        subtotal: subtotal,
        vat: vat,
        discount: discount,
        grandTotal: grandTotal,
        date: new Date().toLocaleDateString(),
        time: new Date().toLocaleTimeString(),
        status: 'completed'
      };

      // Set receipt data and show receipt modal
      setReceiptData(receipt);
      setShowReceiptModal(true);
      
      toast.success('Bulk sale processed successfully!', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });

      // Keep the modal open and form populated for next sale
      // Clear the selected products and reset form for next entry
      setSelectedProducts([]);
      setDiscount(0);
      setRemark('');
      
      // Reset customer information and generate new ID for next entry
      setSelectedCustomer(null);
      setSelectedCustomerName('');
      setCustomerInfo({
        customerId: '',
        phoneNumber: '',
        email: '',
        outstandingAmount: 'Ghs 0.00',
        address: ''
      });
      setCustomerSearchQuery('');
      setShowCustomerDropdown(false);
      
      // Generate new bulk sale ID for the next entry
      generateBulkSaleId();
      
    } catch (error) {
      console.error('Error saving bulk sale:', error);
      toast.error('Failed to save bulk sale. Please try again.', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    } finally {
      setLoading(false);
    }
  }, [selectedCustomer, selectedProducts, bulkSaleId, subtotal, vat, discount, grandTotal, resetModalFields]);

  // Fetch bulk sales history
  const fetchBulkSalesHistory = useCallback(async (searchTerm = null) => {
    setBulkSalesHistoryLoading(true);
    try {
      // Build query parameters
      const params = {
        sort_by: bulkSalesHistorySortBy,
        sort_order: bulkSalesHistorySortOrder
      };

      // Use provided search term or current state
      const finalSearchTerm = searchTerm !== null ? searchTerm : bulkSalesHistorySearchTerm;
      if (finalSearchTerm && finalSearchTerm.trim()) {
        params.search = finalSearchTerm.trim();
      }

      if (bulkSalesHistoryStartDate) {
        params.start_date = bulkSalesHistoryStartDate;
      }
      
      if (bulkSalesHistoryEndDate) {
        params.end_date = bulkSalesHistoryEndDate;
      }

      if (bulkSalesHistoryStatus && bulkSalesHistoryStatus !== 'all') {
        params.status = bulkSalesHistoryStatus;
      }

      const response = await apiService.getBulkSalesHistory(params);
      
      if (response.success) {
        setBulkSalesHistory(response.data.bulk_sales || []);
        setBulkSalesHistoryTotal(response.data.total || 0);
      } else {
        console.error('Error fetching bulk sales history:', response.message);
        toast.error('Failed to load bulk sales history', {
          style: {
            background: '#1f2937',
            color: '#fff',
            border: '1px solid #374151'
          }
        });
      }
    } catch (error) {
      console.error('Error fetching bulk sales history:', error);
      toast.error('Network error while loading bulk sales history', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    } finally {
      setBulkSalesHistoryLoading(false);
    }
  }, [bulkSalesHistorySortBy, bulkSalesHistorySortOrder, bulkSalesHistoryStartDate, bulkSalesHistoryEndDate, bulkSalesHistoryStatus, bulkSalesHistorySearchTerm]);

  // Load data when modal opens
  useEffect(() => {
    if (activeModal === 'bulk-sales-history') {
      fetchBulkSalesHistory();
    }
  }, [activeModal]);

  // Load reports data when bulk-reports modal opens
  useEffect(() => {
    if (activeModal === 'bulk-reports') {
      fetchBulkReportsData();
    }
  }, [activeModal]);

  useEffect(() => {
    // Page changed
  }, [bulkSalesHistoryCurrentPage]);

  // Handle bulk sales history modal open
  const handleBulkSalesHistoryOpen = useCallback(() => {
    handleModalOpen('bulk-sales-history');
  }, []);

  // Debounced search function for bulk sales history
  const debouncedBulkSalesSearch = useCallback(
    debounce((searchTerm) => {
      if (searchTerm.length >= 3 || searchTerm.length === 0) {
        fetchBulkSalesHistory(searchTerm);
      }
    }, 500),
    [fetchBulkSalesHistory]
  );

  // Handle bulk sales history search
  const handleBulkSalesHistorySearch = useCallback((searchTerm) => {
    setBulkSalesHistorySearchTerm(searchTerm);
    debouncedBulkSalesSearch(searchTerm);
  }, [debouncedBulkSalesSearch]);

  // Handle bulk sales history filter change
  const handleBulkSalesHistoryFilterChange = useCallback((filter) => {
    setBulkSalesHistoryFilter(filter);
  }, []);

  // Handle apply bulk sales history filter
  const handleApplyBulkSalesHistoryFilter = useCallback(() => {
    // Filter is handled by useEffect
  }, []);

  // Handle today filter
  const handleTodayFilter = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    setBulkSalesHistoryStartDate(today);
    setBulkSalesHistoryEndDate(today);
    setBulkSalesHistoryStatus('all');
    setBulkSalesHistoryCurrentPage(1);
  }, []);

  // Handle pagination
  const handlePreviousPage = useCallback(async () => {
    if (bulkSalesHistoryCurrentPage > 1) {
      const newPage = bulkSalesHistoryCurrentPage - 1;
      setBulkSalesHistoryCurrentPage(newPage);
      
      // Direct API call with new page
      setBulkSalesHistoryLoading(true);
      try {
        const params = {
          page: newPage,
          per_page: bulkSalesHistoryPerPage,
          sort_by: bulkSalesHistorySortBy,
          sort_order: bulkSalesHistorySortOrder
        };
        
        if (bulkSalesHistoryStartDate) params.start_date = bulkSalesHistoryStartDate;
        if (bulkSalesHistoryEndDate) params.end_date = bulkSalesHistoryEndDate;
        if (bulkSalesHistoryStatus && bulkSalesHistoryStatus !== 'all') params.status = bulkSalesHistoryStatus;
        if (bulkSalesHistorySearchTerm) params.search = bulkSalesHistorySearchTerm;

        const response = await apiService.getBulkSalesHistory(params);
        if (response.success) {
          setBulkSalesHistory(response.data.bulk_sales || []);
          setBulkSalesHistoryTotal(response.data.total || 0);
        }
      } catch (error) {
        console.error('Error fetching page:', error);
      } finally {
        setBulkSalesHistoryLoading(false);
      }
    }
  }, [bulkSalesHistoryCurrentPage, bulkSalesHistoryPerPage, bulkSalesHistorySortBy, bulkSalesHistorySortOrder, bulkSalesHistoryStartDate, bulkSalesHistoryEndDate, bulkSalesHistoryStatus, bulkSalesHistorySearchTerm]);

  const handleNextPage = useCallback(async () => {
    const totalPages = Math.ceil(bulkSalesHistoryTotal / bulkSalesHistoryPerPage);
    if (bulkSalesHistoryCurrentPage < totalPages) {
      const newPage = bulkSalesHistoryCurrentPage + 1;
      setBulkSalesHistoryCurrentPage(newPage);
      
      // Direct API call with new page
      setBulkSalesHistoryLoading(true);
      try {
        const params = {
          page: newPage,
          per_page: bulkSalesHistoryPerPage,
          sort_by: bulkSalesHistorySortBy,
          sort_order: bulkSalesHistorySortOrder
        };
        
        if (bulkSalesHistoryStartDate) params.start_date = bulkSalesHistoryStartDate;
        if (bulkSalesHistoryEndDate) params.end_date = bulkSalesHistoryEndDate;
        if (bulkSalesHistoryStatus && bulkSalesHistoryStatus !== 'all') params.status = bulkSalesHistoryStatus;
        if (bulkSalesHistorySearchTerm) params.search = bulkSalesHistorySearchTerm;

        const response = await apiService.getBulkSalesHistory(params);
        if (response.success) {
          setBulkSalesHistory(response.data.bulk_sales || []);
          setBulkSalesHistoryTotal(response.data.total || 0);
        }
      } catch (error) {
        console.error('Error fetching page:', error);
      } finally {
        setBulkSalesHistoryLoading(false);
      }
    }
  }, [bulkSalesHistoryCurrentPage, bulkSalesHistoryTotal, bulkSalesHistoryPerPage, bulkSalesHistorySortBy, bulkSalesHistorySortOrder, bulkSalesHistoryStartDate, bulkSalesHistoryEndDate, bulkSalesHistoryStatus, bulkSalesHistorySearchTerm]);

  // Handle sorting
  const handleSort = useCallback((column) => {
    if (bulkSalesHistorySortBy === column) {
      setBulkSalesHistorySortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setBulkSalesHistorySortBy(column);
      setBulkSalesHistorySortOrder('asc');
    }
    setBulkSalesHistoryCurrentPage(1); // Reset to first page
  }, [bulkSalesHistorySortBy, bulkSalesHistorySortOrder]);

  // Handle bulk sale selection
  const handleBulkSaleSelect = useCallback((sale) => {
    // TODO: Implement bulk sale details view
    toast.success(`Viewing bulk sale: ${sale.bulk_sale_id}`, {
      style: {
        background: '#1f2937',
        color: '#fff',
        border: '1px solid #374151'
      }
    });
  }, []);

  // Handle print existing receipt
  const handlePrintExistingReceipt = useCallback(async (sale) => {
    try {
      // Fetch complete bulk sale details including items
      const response = await apiService.getBulkSaleDetails(sale.id);
      const saleDetails = response.data;
      
      // Validate that items exist and have the expected structure
      if (!saleDetails.items || !Array.isArray(saleDetails.items)) {
        throw new Error('No items found for this sale');
      }
      
      // Prepare receipt data in the same format as create bulk sale receipt
      const receiptData = {
        bulkSaleId: saleDetails.bulk_sale_id,
        date: new Date(saleDetails.created_at).toLocaleDateString(),
        time: new Date(saleDetails.created_at).toLocaleTimeString(),
        customer: {
          name: saleDetails.customer_name || 'N/A',
          phone: saleDetails.customer_phone || 'N/A',
          email: saleDetails.customer_email || 'N/A',
          address: saleDetails.customer_address || 'N/A'
        },
        items: saleDetails.items.map(item => ({
            name: item.product_name || 'Unknown Product',
            quantity: item.quantity || 0,
            uom: item.uom || 'N/A',
            price: parseFloat(item.unit_price || 0),
            amount: parseFloat(item.total_amount || 0)
          })),
        subtotal: saleDetails.items.reduce((sum, item) => sum + parseFloat(item.total_amount || 0), 0),
        vat: parseFloat(saleDetails.vat || 0),
        discount: parseFloat(saleDetails.discount || 0),
        grandTotal: parseFloat(saleDetails.total_amount || 0)
      };
      
      setReceiptData(receiptData);
      setShowReceiptModal(true);
    } catch (error) {
      console.error('Error fetching sale details:', error);
      console.error('Error details:', error.response?.data || error.message);
      toast.error('Failed to load sale details for printing', {
        duration: 4000,
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#fff',
          border: '1px solid #DC2626'
        }
      });
    }
  }, []);

  // Handle edit sale
  const handleEditSale = useCallback(async (sale) => {
    try {
      setEditLoading(true);
      
      // Fetch complete bulk sale details including items
      const response = await apiService.getBulkSaleDetails(sale.id);
      const saleDetails = response.data;
      
      setSelectedSale(saleDetails);
      
      // Populate form data with fetched details
    setEditFormData({
        customer_name: saleDetails.customer_name || '',
        customer_email: saleDetails.customer_email || '',
        customer_phone: saleDetails.customer_phone || '',
        customer_address: saleDetails.customer_address || '',
        company_name: companySettings.company_name || 'Grovalue Enterprise',
        sales_agent: saleDetails.sales_agent || '',
        sales_date: new Date(saleDetails.created_at).toLocaleDateString('en-GB'),
        mode_of_sales: saleDetails.mode_of_sales || 'Cash',
        remark: saleDetails.remark || '',
        total_amount: saleDetails.total_amount || '',
        discount: saleDetails.discount || 0
      });
      
      // Populate selected products with fetched items
      if (saleDetails.items && saleDetails.items.length > 0) {
        const formattedProducts = saleDetails.items.map(item => ({
          id: item.product_id,
          name: item.product_name,
          quantity: parseFloat(item.quantity),
          uom: item.uom,
          price: parseFloat(item.unit_price),
          amount: parseFloat(item.total_amount)
        }));
        setSelectedProducts(formattedProducts);
      } else {
    setSelectedProducts([]);
      }
      
      // Set discount
      setDiscount(saleDetails.discount || 0);
      
    setShowEditModal(true);
      
    } catch (error) {
      console.error('Error fetching bulk sale details:', error);
      toast.error('Failed to load bulk sale details', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    } finally {
      setEditLoading(false);
    }
  }, []);

  // Handle edit form submission
  const handleEditSubmit = useCallback(async () => {
    if (!selectedSale) return;
    
    setEditLoading(true);
    try {
      // Calculate new total with discount and products
      const productsTotal = selectedProducts.reduce((sum, product) => sum + parseFloat(product.amount || 0), 0);
      const discountAmount = parseFloat(editFormData.discount) || 0;
      const newTotal = productsTotal - discountAmount;
      
      // Prepare data for API call
      const updateData = {
        customer_name: editFormData.customer_name,
        customer_email: editFormData.customer_email,
        customer_phone: editFormData.customer_phone,
        customer_address: editFormData.customer_address,
        company_name: editFormData.company_name,
        sales_agent: editFormData.sales_agent,
        sales_date: editFormData.sales_date,
        mode_of_sales: editFormData.mode_of_sales,
        remark: editFormData.remark,
        discount: discountAmount,
        status: 'completed',
        items: selectedProducts.map(product => ({
          product_id: product.id,
          product_name: product.name,
          quantity: product.quantity,
          uom: product.uom,
          unit_price: product.price,
          total_amount: product.amount
        }))
      };
      

      
      // Call API to update the bulk sale
      const response = await apiService.updateBulkSale(selectedSale.id, updateData);
      
      if (response.success) {
      // Update the sale in the list
      setBulkSalesHistory(prev => prev.map(sale => 
        sale.id === selectedSale.id 
          ? {
              ...sale,
              customer_name: editFormData.customer_name,
              customer_email: editFormData.customer_email,
              customer_phone: editFormData.customer_phone,
              customer_address: editFormData.customer_address,
              sales_agent: editFormData.sales_agent,
              mode_of_sales: editFormData.mode_of_sales,
              remark: editFormData.remark,
                total_amount: response.data.grand_total.toString(),
              discount: discountAmount
            }
          : sale
      ));
      
      // Update selected sale
      setSelectedSale(prev => ({
        ...prev,
        customer_name: editFormData.customer_name,
        customer_email: editFormData.customer_email,
        customer_phone: editFormData.customer_phone,
        customer_address: editFormData.customer_address,
        sales_agent: editFormData.sales_agent,
        mode_of_sales: editFormData.mode_of_sales,
        remark: editFormData.remark,
          total_amount: response.data.grand_total.toString(),
        discount: discountAmount
      }));
      
      setShowEditModal(false);
        toast.success(`Bulk sale ${selectedSale.bulk_sale_id} updated successfully! 🎉`, {
          duration: 3000,
          icon: '✅',
        style: {
            background: '#10B981',
          color: '#fff',
            border: '1px solid #059669'
        }
      });
      } else {
        throw new Error(response.message || 'Failed to update bulk sale');
      }
    } catch (error) {
      console.error('Error updating bulk sale:', error);
      toast.error('Failed to update bulk sale. Please try again.', {
        duration: 4000,
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#fff',
          border: '1px solid #DC2626'
        }
      });
    } finally {
      setEditLoading(false);
    }
  }, [selectedSale, editFormData, selectedProducts]);

  // Bulk Reports Functions
  const handleReportsDateChange = useCallback((field, value) => {
    setReportsDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleApplyReportsFilter = useCallback(async () => {
    setReportsLoading(true);
    try {
      // Fetch reports data based on date range
      await fetchBulkReportsData();
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to fetch reports data');
    } finally {
      setReportsLoading(false);
    }
  }, []);

  const handleTodayReportsFilter = useCallback(async () => {
    const today = new Date().toISOString().split('T')[0];
    setReportsDateRange({
      startDate: today,
      endDate: today
    });
    
    setReportsLoading(true);
    try {
      await fetchBulkReportsData();
    } catch (error) {
      console.error('Error fetching today\'s reports:', error);
      toast.error('Failed to fetch today\'s reports');
    } finally {
      setReportsLoading(false);
    }
  }, []);

  const fetchBulkReportsData = useCallback(async () => {
    try {
      // Fetch dashboard stats
      const dashboardResponse = await apiService.getBulkReportsDashboard({
        start_date: reportsDateRange.startDate,
        end_date: reportsDateRange.endDate
      });
      
      if (dashboardResponse.success) {
        setReportsData(prev => ({
          ...prev,
          dashboardStats: dashboardResponse.data
        }));
      }

      // Fetch sales report
      const salesResponse = await apiService.getBulkReportsSales({
        start_date: reportsDateRange.startDate,
        end_date: reportsDateRange.endDate
      });
      
      if (salesResponse.success) {
        setReportsData(prev => ({
          ...prev,
          salesReport: salesResponse.data
        }));
      }

      // Fetch products report
      const productsResponse = await apiService.getBulkReportsProducts({
        start_date: reportsDateRange.startDate,
        end_date: reportsDateRange.endDate
      });
      
      if (productsResponse.success) {
        setReportsData(prev => ({
          ...prev,
          productsReport: productsResponse.data
        }));
      }

      // Fetch customers report
      const customersResponse = await apiService.getBulkReportsCustomers({
        start_date: reportsDateRange.startDate,
        end_date: reportsDateRange.endDate
      });
      
      if (customersResponse.success) {
        setReportsData(prev => ({
          ...prev,
          customersReport: customersResponse.data
        }));
      }
    } catch (error) {
      console.error('Error fetching bulk reports data:', error);
      throw error;
    }
  }, [reportsDateRange.startDate, reportsDateRange.endDate]);

  // Handle delete sale
  const handleDeleteSale = useCallback((sale) => {
    setSelectedSale(sale);
    setShowBulkSaleDeleteModal(true);
  }, []);

  // Handle export to Excel
  const handleExportToExcel = useCallback(() => {
    try {
      // Create CSV content
      const headers = ['BulkSale ID', 'Customer Name', 'Email', 'Phone Number', 'Amount', 'Status', 'Date'];
      const csvContent = [
        headers.join(','),
        ...bulkSalesHistory.map(sale => [
          sale.bulk_sale_id,
          sale.customer_name || 'N/A',
          sale.customer_email || 'N/A',
          sale.customer_phone || 'N/A',
          formatCurrency(sale.total_amount),
          sale.status || 'N/A',
          new Date(sale.created_at).toLocaleDateString()
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `bulk_sales_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Excel file exported successfully!', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export Excel file', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    }
  }, [bulkSalesHistory]);

  // Handle export to PDF
  const handleExportToPDF = useCallback(() => {
    try {
      // Create HTML content for PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Bulk Sales Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; text-align: center; }
            .date { text-align: center; color: #666; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .amount { color: #008000; font-weight: bold; }
            .status { padding: 4px 8px; border-radius: 4px; font-size: 12px; }
            .completed { background-color: #d4edda; color: #155724; }
            .pending { background-color: #fff3cd; color: #856404; }
            .cancelled { background-color: #f8d7da; color: #721c24; }
          </style>
        </head>
        <body>
          <h1>Bulk Sales Report</h1>
          <div class="date">Generated on: ${new Date().toLocaleDateString()}</div>
          <table>
            <thead>
              <tr>
                <th>BulkSale ID</th>
                <th>Customer Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              ${bulkSalesHistory.map(sale => `
                <tr>
                  <td>${sale.bulk_sale_id}</td>
                  <td>${sale.customer_name || 'N/A'}</td>
                  <td>${sale.customer_email || 'N/A'}</td>
                  <td>${sale.customer_phone || 'N/A'}</td>
                  <td class="amount">${formatCurrency(sale.total_amount)}</td>
                  <td><span class="status ${sale.status || 'pending'}">${sale.status || 'N/A'}</span></td>
                  <td>${new Date(sale.created_at).toLocaleDateString()}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
        </html>
      `;

      // Create blob and download
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bulk_sales_${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Report exported successfully! Open in browser and print as PDF', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report', {
        style: {
          background: '#1f2937',
          color: '#fff',
          border: '1px solid #374151'
        }
      });
    }
  }, [bulkSalesHistory]);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setActiveModal(null);
    resetModalFields();
  }, [resetModalFields]);

  // Fetch currency and company settings
  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/settings/receipt/info`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCurrencySettings({
            currency_symbol: data.data.currency_symbol || '₵',
            currency_code: data.data.currency_code || 'GHS'
          });
          setCompanySettings({
            company_name: data.data.company_name || 'Grovalue Enterprise',
            company_address: data.data.company_address || '123 Business Street, City, Country',
            company_phone: data.data.company_phone || '+233 XX XXX XXXX',
            company_email: data.data.company_email || '',
            company_website: data.data.company_website || '',
            tax_number: data.data.tax_number || ''
          });
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  // Format currency function
  const formatCurrency = useCallback((amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return `${currencySettings.currency_symbol} 0.00`;
    }
    
    const numAmount = parseFloat(amount);
    if (numAmount === 0) {
      return `${currencySettings.currency_symbol} 0.00`;
    }
    
    // Format with commas for thousands and 2 decimal places
    return `${currencySettings.currency_symbol} ${numAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }, [currencySettings.currency_symbol]);

  // Fetch settings on component mount
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Update editFormData company_name when companySettings changes
  useEffect(() => {
    if (companySettings.company_name) {
      setEditFormData(prev => ({
        ...prev,
        company_name: companySettings.company_name
      }));
    }
  }, [companySettings.company_name]);

  // Receipt Modal Functions
  const handlePrintReceipt = useCallback(() => {
    try {
      // Create professional receipt HTML content
      const receiptContent = `
        <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; background: white;">
          <!-- Header Section -->
          <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #2563eb; padding-bottom: 20px;">
            <h1 style="font-size: 32px; font-weight: bold; color: #1f2937; margin: 0 0 8px 0; letter-spacing: 2px;">{APP_NAME}</h1>
            <p style="font-size: 16px; color: #6b7280; margin: 0 0 15px 0; font-weight: 500;">Bulk Sale Receipt</p>
            <div style="display: flex; justify-content: space-between; align-items: center; font-size: 12px; color: #6b7280;">
              <span><strong>Receipt ID:</strong> ${receiptData.bulkSaleId}</span>
              <span><strong>Date:</strong> ${receiptData.date}</span>
              <span><strong>Time:</strong> ${receiptData.time}</span>
              <span><strong>Sales by:</strong> ${user?.name || user?.username || 'Agent'}</span>
            </div>
          </div>

          <!-- Bill To and Bill From Section -->
          <div style="margin-bottom: 30px; display: flex; gap: 30px;">
            <!-- Bill To Section (Left) -->
            <div style="flex: 1;">
              <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Bill To</h3>
              <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0;">
                <div style="font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 8px;">${receiptData.customer.name}</div>
                <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📞 ${receiptData.customer.phone}</div>
                <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">✉️ ${receiptData.customer.email}</div>
                <div style="font-size: 14px; color: #6b7280;">📍 ${receiptData.customer.address}</div>
              </div>
            </div>
            
            <!-- Bill From Section (Right) -->
            <div style="flex: 1;">
              <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Bill From</h3>
              <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0;">
                <div style="font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 8px;">${companySettings.company_name}</div>
                <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📍 ${companySettings.company_address}</div>
                <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📞 ${companySettings.company_phone}</div>
                ${companySettings.tax_number ? `<div style="font-size: 14px; color: #6b7280;">Tax ID: ${companySettings.tax_number}</div>` : ''}
              </div>
            </div>
          </div>

          <!-- Items Table -->
          <div style="margin-bottom: 30px;">
            <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Items Purchased</h3>
            <table style="width: 100%; border-collapse: collapse; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
              <thead>
                <tr style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);">
                  <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: left; font-weight: bold; color: #374151; font-size: 14px;">Product</th>
                  <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Qty</th>
                  <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">UoM</th>
                  <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Unit Price</th>
                  <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Total</th>
                </tr>
              </thead>
              <tbody>
                ${receiptData.items.map((item, index) => `
                  <tr style="${index % 2 === 0 ? 'background: #f8fafc;' : 'background: white;'}">
                    <td style="border: 1px solid #e2e8f0; padding: 12px; font-weight: 500; color: #1f2937; font-size: 14px;">${item.name}</td>
                    <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #374151; font-size: 14px;">${item.quantity}</td>
                    <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #374151; font-size: 14px;">${item.uom}</td>
                    <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; color: #374151; font-size: 14px;">${formatCurrency(item.price)}</td>
                    <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #1f2937; font-size: 14px;">${formatCurrency(item.amount)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>

          <!-- Payment Summary -->
          <div style="background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%); padding: 25px; border-radius: 12px; border: 1px solid #e2e8f0;">
            <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 20px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Payment Summary</h3>
            <div style="space-y: 12px;">
              <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #e2e8f0;">
                <span style="font-size: 16px; color: #6b7280; font-weight: 500;">Subtotal:</span>
                <span style="font-size: 16px; font-weight: bold; color: #1f2937;">${formatCurrency(receiptData.subtotal)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #e2e8f0;">
                <span style="font-size: 16px; color: #6b7280; font-weight: 500;">VAT (15%):</span>
                <span style="font-size: 16px; font-weight: bold; color: #1f2937;">${formatCurrency(receiptData.vat)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #e2e8f0;">
                <span style="font-size: 16px; color: #6b7280; font-weight: 500;">Discount:</span>
                <span style="font-size: 16px; font-weight: bold; color: #dc2626;">-${formatCurrency(receiptData.discount)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; padding: 15px 0; border-top: 2px solid #2563eb; margin-top: 15px;">
                <span style="font-size: 20px; font-weight: bold; color: #1f2937;">Grand Total:</span>
                <span style="font-size: 24px; font-weight: bold; color: #059669;">${formatCurrency(receiptData.grandTotal)}</span>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 2px solid #e2e8f0; color: #6b7280; font-size: 12px;">
            <p style="margin: 0 0 8px 0;">Thank you for your business!</p>
            <p style="margin: 0 0 8px 0;">Keep this receipt for your records</p>
            <p style="margin: 0; font-weight: bold;">Powered By SherSoft Technologies - 0548104944</p>
          </div>
        </div>
      `;

      // Create hidden iframe for printing without opening new tab
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.top = '-9999px';
      iframe.style.width = '210mm';
      iframe.style.height = '297mm';
      iframe.style.border = 'none';
      
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(`
        <html>
          <head>
            <title>Bulk Sale Receipt - ${receiptData.bulkSaleId}</title>
            <style>
              @page {
                size: A4;
                margin: 20mm;
              }
              body { 
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
                margin: 0; 
                padding: 0;
                background: white;
                color: #1f2937;
              }
              .receipt { 
                width: 100%; 
                margin: 0 auto; 
                padding: 0;
              }
              @media print {
                body { margin: 0; padding: 0; }
                .receipt { border: none; }
              }
            </style>
          </head>
          <body>
            <div class="receipt">${receiptContent}</div>
          </body>
        </html>
      `);
      iframeDoc.close();
      
      // Wait for content to load, then print
      iframe.onload = () => {
        iframe.contentWindow.print();
        // Clean up iframe after printing
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      
      // Show print notification
      toast.success('Bulk sale receipt sent to printer!', {
        duration: 2000,
        icon: '🖨️',
        style: {
          background: '#10B981',
          color: '#fff',
          fontSize: '14px',
          padding: '12px',
          borderRadius: '8px',
        },
      });
      
    } catch (error) {
      console.error('Error printing bulk sale receipt:', error);
      toast.error('Failed to print bulk sale receipt', {
        duration: 3000,
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#fff',
          fontSize: '14px',
          padding: '12px',
          borderRadius: '8px',
        },
      });
    }
  }, [receiptData, user, formatCurrency, companySettings]);

  const handleCloseReceipt = useCallback(() => {
    setShowReceiptModal(false);
    setReceiptData(null);
    // Keep the main bulk sale modal open - don't reset or close it
  }, []);

  // Product search functionality
  const searchProducts = useCallback(async (query) => {
    try {
      setSearching(true);
      const data = await apiService.searchProducts(query);
      setProducts(data.products || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setProducts([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Get all products functionality
  const getAllProducts = useCallback(async () => {
    try {
      setSearching(true);
      const data = await apiService.getAllProducts();
      setProducts(data.products || []);
      setShowProductDropdown(true);
    } catch (error) {
      console.error('Error fetching all products:', error);
      setProducts([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((query) => {
      searchProducts(query);
    }, 300),
    [searchProducts]
  );

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    const query = e.target.value.toUpperCase();
    setSearchQuery(query);
    setSelectedIndex(-1);
    
    // Only show dropdown when actively searching (2+ characters)
    if (query.trim().length >= 2) {
      setShowProductDropdown(true);
      debouncedSearch(query);
    } else {
      setShowProductDropdown(false);
      setProducts([]);
    }
  }, [debouncedSearch]);

  // Handle product selection
  const handleProductSelect = useCallback((product) => {
    setSearchQuery(product.name);
    setSelectedProduct(product);
    setCurrentProductQuantity(product.quantity || product.qty || 0);
    setProducts([]);
    setShowProductDropdown(false);
    setSelectedIndex(-1);
    setWorkflowStep('quantity');
    
    // Focus quantity field
    setTimeout(() => {
      const quantityInput = document.getElementById('quantity-input');
      if (quantityInput) {
        quantityInput.focus();
        quantityInput.select();
        quantityInput.style.border = '2px solid #3b82f6';
        quantityInput.style.backgroundColor = '#dbeafe';
        quantityInput.style.boxShadow = '0 0 8px rgba(59, 130, 246, 0.3)';
        
        setTimeout(() => {
          quantityInput.style.border = '';
          quantityInput.style.backgroundColor = '';
          quantityInput.style.boxShadow = '';
        }, 2000);
      }
    }, 100);
  }, []);

  // Handle quantity change
  const handleQuantityChange = useCallback((productId, quantity) => {
    setSelectedProducts(prev => prev.map(product => 
      product.id === productId 
        ? { ...product, quantity: parseFloat(quantity) || 0, amount: (parseFloat(product.price) * (parseFloat(quantity) || 0)) }
        : product
    ));
  }, []);

  // Handle UOM change
  const handleUOMChange = useCallback((productId, uom) => {
    setSelectedProducts(prev => prev.map(product => {
      if (product.id === productId) {
        // Update price based on UOM selection
        let newPrice = product.price;
        if (uom === 'Ctn' && product.selling_price_ctn) {
          newPrice = parseFloat(product.selling_price_ctn);
        } else if (uom === 'Pcs' && product.selling_price_pcs) {
          newPrice = parseFloat(product.selling_price_pcs);
        }
        
        return { 
          ...product, 
          uom,
          price: newPrice,
          amount: newPrice * product.quantity
        };
      }
      return product;
    }));
  }, []);

  // Remove product from table
  const removeProduct = useCallback((productId, uom) => {
    setSelectedProducts(prev => prev.filter(product => !(product.id === productId && product.uom === uom)));
    setShowDeleteModal(false);
    setProductToDelete(null);
  }, []);

  // Increase quantity
  const increaseQuantity = useCallback((productId, uom) => {
    setSelectedProducts(prev => prev.map(product => 
      (product.id === productId && product.uom === uom)
        ? { ...product, quantity: product.quantity + 1, amount: product.price * (product.quantity + 1) }
        : product
    ));
  }, []);

  // Decrease quantity
  const decreaseQuantity = useCallback((productId, uom) => {
    setSelectedProducts(prev => prev.map(product => {
      if (product.id === productId && product.uom === uom && product.quantity > 1) {
        return { ...product, quantity: product.quantity - 1, amount: product.price * (product.quantity - 1) };
      }
      return product;
    }));
  }, []);

  // Show delete confirmation
  const showDeleteConfirmation = useCallback((product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  }, []);

  // Add product to cart
  const addToCart = useCallback(() => {
    const quantityInput = document.getElementById('quantity-input');
    const uomSelect = document.getElementById('uom-select');
    
    if (quantityInput && uomSelect && selectedProduct) {
      const quantity = parseFloat(quantityInput.value) || 1;
      const uom = uomSelect.value;
      
      // Check if product already exists with the same UoM
      const existingProductIndex = selectedProducts.findIndex(p => p.id === selectedProduct.id && p.uom === uom);
      
      if (existingProductIndex !== -1) {
        // Product exists with same UoM - increase quantity
        setSelectedProducts(prev => prev.map((product, index) => {
          if (index === existingProductIndex) {
            const newQuantity = product.quantity + quantity;
            return {
              ...product,
              quantity: newQuantity,
              amount: product.price * newQuantity
            };
          }
          return product;
        }));
      } else {
        // Product doesn't exist or has different UoM - add as new entry
        setSelectedProducts(prev => [{
          ...selectedProduct,
          quantity,
          uom,
          price: uom === 'Ctn' ? parseFloat(selectedProduct.selling_price_ctn || 0) : parseFloat(selectedProduct.selling_price_pcs || 0),
          amount: (uom === 'Ctn' ? parseFloat(selectedProduct.selling_price_ctn || 0) : parseFloat(selectedProduct.selling_price_pcs || 0)) * quantity
        }, ...prev]);
      }
      
      // Reset workflow
      setSearchQuery('');
      setSelectedProduct(null);
      setProducts([]);
      setShowProductDropdown(false);
      setSelectedIndex(-1);
      setWorkflowStep('search');
      
      // Reset inputs
      quantityInput.value = '1';
      uomSelect.value = 'Ctn';
      
      // Focus back to search
      setTimeout(() => {
        const searchInput = document.querySelector('input[placeholder*="Search products"]');
        if (searchInput) {
          searchInput.focus();
        }
      }, 100);
    }
  }, [selectedProduct, selectedProducts]);

  // Handle key navigation in search
  const handleKeyDown = useCallback((e) => {
    if (!showProductDropdown || products.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = selectedIndex < products.length - 1 ? selectedIndex + 1 : 0;
        setSelectedIndex(nextIndex);
        // Scroll selected item into view
        setTimeout(() => {
          const selectedElement = document.querySelector(`[data-index="${nextIndex}"]`);
          if (selectedElement) {
            selectedElement.scrollIntoView({ 
              block: 'nearest', 
              behavior: 'smooth' 
            });
          }
        }, 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = selectedIndex > 0 ? selectedIndex - 1 : products.length - 1;
        setSelectedIndex(prevIndex);
        // Scroll selected item into view
        setTimeout(() => {
          const selectedElement = document.querySelector(`[data-index="${prevIndex}"]`);
          if (selectedElement) {
            selectedElement.scrollIntoView({ 
              block: 'nearest', 
              behavior: 'smooth' 
            });
          }
        }, 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < products.length) {
          handleProductSelect(products[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowProductDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  }, [showProductDropdown, products, selectedIndex, handleProductSelect]);

  // Handle search focus
  const handleSearchFocus = useCallback(() => {
    if (searchQuery.trim().length >= 2) {
      setShowProductDropdown(true);
    }
  }, [searchQuery]);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close product dropdown when clicking outside
      if (showProductDropdown && !event.target.closest('.product-search-container')) {
        setShowProductDropdown(false);
      }
      // Close customer dropdown when clicking outside
      if (showCustomerDropdown && !event.target.closest('.customer-dropdown-container')) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProductDropdown, showCustomerDropdown]);

  return (
    <Layout>
      <main className="flex-1 p-6 overflow-y-auto">
            <div className="max-w-7xl mx-auto space-y-8">
              {/* Page Header */}
              
              {/* Bulk Sales Content */}
              <div className="bg-white rounded-xl p-8 mt-6 shadow-sm border border-gray-100">
                {/* Header */}
                <div className="text-center mb-12">
                  <h1 className="text-4xl font-bold text-gray-900 mb-4">Bulk Sales Management</h1>
                  <p className="text-xl text-gray-600">Process bulk orders and manage wholesale transactions efficiently</p>
                </div>

                {/* Four Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                  {/* Create Bulk Sale */}
                  <div className="bg-gradient-to-br from-white to-indigo-50/40 rounded-2xl p-8 border border-indigo-100 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-indigo-400 via-indigo-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg">
                        <Package size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">Create Bulk Sale</h3>
                        <p className="text-gray-600">Create a new bulk sale order for wholesale customers</p>
                      </div>
                    </div>
                                          <button 
                        onClick={() => handleModalOpen('create-bulk-sale')}
                        className="w-full bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
                      >
                        Create New Order
                      </button>
                  </div>

                  {/* Bulk Sales History */}
                  <div className="bg-gradient-to-br from-white to-green-50/40 rounded-2xl p-8 border border-green-100 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-green-400 via-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                        <Clock size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">Bulk Sales History</h3>
                        <p className="text-gray-600">View and manage all bulk sales transactions</p>
                      </div>
                    </div>
                                          <button 
                        onClick={handleBulkSalesHistoryOpen}
                        className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
                      >
                        View Bulk Sales History
                      </button>
            </div>

                  {/* Order Management */}
                  <div className="bg-gradient-to-br from-white to-purple-50/40 rounded-2xl p-8 border border-purple-100 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-purple-400 via-purple-500 to-violet-500 rounded-xl flex items-center justify-center shadow-lg">
                        <ShoppingCart size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">Order Management</h3>
                        <p className="text-gray-600">Track and manage bulk order status</p>
                      </div>
                    </div>
                                          <button 
                        onClick={() => handleModalOpen('order-management')}
                        className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
                      >
                        Manage Orders
                      </button>
                  </div>

                  {/* Bulk Reports */}
                  <div className="bg-gradient-to-br from-white to-orange-50/40 rounded-2xl p-8 border border-orange-100 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                        <BarChart3 size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">Bulk Reports</h3>
                        <p className="text-gray-600">Generate detailed bulk sales analytics and reports</p>
                      </div>
                    </div>
                                          <button 
                        onClick={() => handleModalOpen('bulk-reports')}
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
                      >
                        Generate Reports
                      </button>
                  </div>
                </div>
              </div>
            </div>
          </main>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Sticky Background */}
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300"></div>
          
          {/* Modal Content */}
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative w-full max-w-7xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col max-h-[95vh]">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  {/* Left side - only show for create-bulk-sale modal */}
                  {activeModal === 'create-bulk-sale' && (
                  <div className="flex items-center space-x-4">
                    <button 
                      onClick={handleSaveBulkSale}
                      disabled={loading}
                        className="bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 hover:from-green-600 hover:via-emerald-600 hover:to-teal-600 disabled:from-gray-400 disabled:via-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl flex items-center space-x-3 transition-all duration-300 transform hover:scale-105 hover:shadow-lg disabled:transform-none disabled:shadow-none font-semibold text-sm border-0 focus:outline-none focus:ring-4 focus:ring-green-300 focus:ring-opacity-50"
                    >
                      <FileText size={16} />
                      <span>{loading ? 'Saving...' : 'Save'}</span>
                    </button>
                    <div className="bg-gray-800 px-4 py-2 rounded-lg font-mono text-sm text-white">
                      {loading ? (
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Generating ID...</span>
                    </div>
                      ) : (
                        <span className="font-bold">
                          {bulkSaleId || `BULK - ${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}${String(new Date().getDate()).padStart(2, '0')}001`}
                        </span>
                      )}
                    </div>
                  </div>
                  )}
                  
                  {/* Center - show modal title for other modals */}
                  {activeModal !== 'create-bulk-sale' && (
                  <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-gradient-to-r from-green-400 via-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                        <BarChart3 size={20} className="text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white tracking-tight">
                          {activeModal === 'bulk-reports' ? 'Bulk Reports' : 
                           activeModal === 'bulk-sales-history' ? 'Bulk Sales History' : 
                           'Bulk Sales'}
                        </h2>
                        <p className="text-white/80 text-sm">
                          {activeModal === 'bulk-reports' ? 'View detailed analytics and reports' : 
                           activeModal === 'bulk-sales-history' ? 'View and manage all bulk sales transactions' : 
                           'Create and manage bulk sales'}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Right side - customer dropdown for create-bulk-sale, close button for all */}
                  <div className="flex items-center space-x-4">
                    {/* Only show customer dropdown for create-bulk-sale modal */}
                    {activeModal === 'create-bulk-sale' && (
                      <div className="relative customer-dropdown-container w-64">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowCustomerDropdown(!showCustomerDropdown);
                          }}
                          className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm text-left flex items-center justify-between min-w-0"
                        >
                          <span className={selectedCustomerName ? 'text-gray-900' : 'text-gray-500'} 
                                style={{ 
                                  overflow: 'hidden', 
                                  textOverflow: 'ellipsis', 
                                  whiteSpace: 'nowrap',
                                  maxWidth: '200px'
                                }}>
                            {selectedCustomerName || 'Choose a customer...'}
                          </span>
                          <svg className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {/* Customer Dropdown */}
                        {showCustomerDropdown && (
                          <div 
                            className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {/* Search Input */}
                            <div className="p-2 border-b border-gray-200">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                                <input
                                  type="text"
                                  value={customerSearchQuery}
                                  onChange={(e) => setCustomerSearchQuery(e.target.value)}
                                  placeholder="Search customers..."
                                  className="w-full pl-8 pr-3 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            </div>
                            
                            {/* Customer Count */}
                            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600 font-medium">
                              {filteredCustomers.length} of {customers.length} customers
                            </div>
                            
                            {/* Customer List */}
                            {filteredCustomers.length > 0 ? (
                              filteredCustomers.map((customer) => (
                                <div
                                  key={customer.id}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleCustomerSelect(customer.id);
                                  }}
                                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 text-sm transition-colors"
                                >
                                  <div className="font-medium text-gray-900">
                                    {customer.name || customer.customer_name}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                                {customers.length === 0 ? 'No customers loaded' : 'No customers found'}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                  <button
                    onClick={handleModalClose}
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                      Close
                  </button>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {activeModal === 'create-bulk-sale' && (
                  <div className="flex-1 flex flex-col">
                    {/* Fixed Content - Scrollable on small screens, fixed on large screens */}
                    <div className="p-2 sm:p-3 lg:p-4 space-y-3 sm:space-y-4 lg:space-y-6 flex-shrink-0 overflow-y-auto lg:overflow-visible">
                      {/* Information Sections */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                        {/* Company Information */}
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-2 sm:p-3 lg:p-4 border border-blue-200">
                          <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center space-x-2">
                            <div className="w-5 h-5 bg-blue-500 rounded-lg flex items-center justify-center">
                              <Package size={14} className="text-white" />
                            </div>
                            <span>Company's Information</span>
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Company (Store) Name</label>
                              <input 
                                type="text" 
                                defaultValue={companySettings.company_name || "Grovalue Enterprise"}
                                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                      </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Sales by / Agent</label>
                              <input 
                                type="text" 
                                value={user?.name || user?.username || user?.email || 'System User'}
                                readOnly
                                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Sales Date</label>
                              <div className="relative">
                                <input 
                                  type="text" 
                                  defaultValue={new Date().toLocaleDateString('en-GB')}
                                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8 text-sm"
                                />
                                <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Mode of Sales</label>
                              <select className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm">
                                <option>Cash</option>
                                <option>Credit</option>
                                <option>Bank Transfer</option>
                                <option>Mobile Money</option>
                        </select>
                      </div>
                            <div className="sm:col-span-2">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Remark</label>
                              <input 
                                type="text" 
                                value={remark}
                                onChange={(e) => setRemark(e.target.value)}
                                placeholder="Optional note..."
                                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                    </div>
                          </div>
                        </div>

                        {/* Customer Information */}
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-2 sm:p-3 lg:p-4 border border-green-200">
                          <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center space-x-2">
                            <div className="w-5 h-5 bg-green-500 rounded-lg flex items-center justify-center">
                              <User size={14} className="text-white" />
                            </div>
                            <span>Customer's Information</span>
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Customer's ID</label>
                              <input 
                                type="text" 
                                value={customerInfo.customerId}
                                placeholder="Customer ID"
                                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                                readOnly
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Phone Number</label>
                              <input 
                                type="tel" 
                                value={customerInfo.phoneNumber}
                                placeholder="Phone number"
                                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                                readOnly
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Email (optional)</label>
                              <input 
                                type="email" 
                                value={customerInfo.email}
                                placeholder="Email address"
                                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                                readOnly
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Outstanding Amount</label>
                              <input 
                                type="text" 
                                value={customerInfo.outstandingAmount}
                                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                                readOnly
                              />
                            </div>
                            <div className="sm:col-span-2">
                              <label className="block text-xs font-medium text-gray-700 mb-1">Customer's Address</label>
                              <input 
                                type="text" 
                                value={customerInfo.address}
                                placeholder="Street, City, Region..."
                                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                                              {/* Product Search Section - Fixed */}
                        <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl p-2 sm:p-3 lg:p-4 border border-purple-200">
                                                  {/* Search Bar */}
                          <div className="bg-white rounded-xl p-2 sm:p-3 lg:p-4 border border-purple-200 shadow-sm mb-3 sm:mb-4">
                                                      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 sm:gap-3 lg:gap-4 items-end">
                            <div className="md:col-span-3">
                                                              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                                                Search Products
                                                                {selectedProduct && (
                                                                  <span className="ml-2 text-xs text-blue-600 font-medium">
                                                                    → {workflowStep === 'quantity' ? 'Enter Quantity' : workflowStep === 'uom' ? 'Select UOM' : 'Ready'}
                                                                  </span>
                                                                )}
                                                              </label>
                              <div className="relative product-search-container">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                                <input 
                                  type="text" 
                                  value={searchQuery}
                                  onChange={handleSearchChange}
                                  onKeyDown={handleKeyDown}
                                  onFocus={handleSearchFocus}
                                  placeholder="Search products by Name and ProductID..."
                                  className="w-full pl-10 pr-4 py-2 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm uppercase"
                                />
                                <button
                                  onClick={getAllProducts}
                                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white text-xs font-medium bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded transition-colors"
                                  title="Show all products"
                                >
                                  All
                          </button>
                                {searching && (
                                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        </div>
                                )}
                                {/* Search Results Dropdown */}
                                {products.length > 0 && showProductDropdown && !isQuantityFocused && !isUomFocused && !selectedProduct && (
                                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto">
                                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600 font-medium">
                                      {searchQuery.trim() ? `Found ${products.length} products` : `Showing all ${products.length} products`}
                        </div>
                                    {products.map((product, index) => (
                                      <div
                                        key={product.id}
                                        data-index={index}
                                        className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150 ${
                                          index === selectedIndex 
                                            ? (selectedIndex % 2 === 0 ? 'bg-red-100 border-red-300' : 'bg-blue-100 border-blue-300')
                                            : 'hover:bg-gray-50'
                                        }`}
                                        onClick={() => handleProductSelect(product)}
                                      >
                                        <div className="flex justify-between items-center">
                                          <div className="flex-1">
                                            <div className="font-medium text-gray-900 text-sm leading-tight">
                                              {product.name}
                      </div>
                    </div>
                                          <div className="flex items-center space-x-3 ml-4">
                                            <div className="text-blue-600 font-semibold text-sm">
                                              {formatCurrency(product.selling_price_ctn || 0)}
                                            </div>
                                            <div className="text-green-600 font-medium text-xs">
                                              {formatCurrency(product.selling_price_pcs || 0)}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">(Qty)</label>
                              <input 
                                id="quantity-input"
                                type="number" 
                                defaultValue="1"
                                min="0"
                                step="0.01"
                                onFocus={(e) => {
                                  setIsQuantityFocused(true);
                                  e.target.select(); // Highlight the text
                                }}
                                onBlur={() => setIsQuantityFocused(false)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    setWorkflowStep('uom');
                                    const uomSelect = document.getElementById('uom-select');
                                    if (uomSelect) {
                                      uomSelect.focus();
                                      uomSelect.style.border = '2px solid #3b82f6';
                                      uomSelect.style.backgroundColor = '#dbeafe';
                                      uomSelect.style.boxShadow = '0 0 8px rgba(59, 130, 246, 0.3)';
                                      
                                      setTimeout(() => {
                                        uomSelect.style.border = '';
                                        uomSelect.style.backgroundColor = '';
                                        uomSelect.style.boxShadow = '';
                                      }, 2000);
                                    }
                                  }
                                }}
                                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">(UoM)</label>
                              <select 
                                id="uom-select"
                                tabIndex="0"
                                onFocus={(e) => {
                                  e.target.focus();
                                  setIsUomFocused(true);
                                  e.target.style.border = '2px solid #3b82f6';
                                  e.target.style.backgroundColor = '#dbeafe';
                                }}
                                onBlur={(e) => {
                                  setIsUomFocused(false);
                                  e.target.style.border = '';
                                  e.target.style.backgroundColor = '';
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (selectedProduct && workflowStep === 'uom') {
                                      addToCart();
                                    }
                                  }
                                  if (e.key === ' ') {
                                    e.preventDefault();
                                  }
                                }}
                                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                              >
                                <option value="Ctn">Ctn</option>
                                <option value="Pcs">Pcs</option>
                                <option value="Kg">Kg</option>
                                <option value="Ltr">Ltr</option>
                                <option value="Box">Box</option>
                                <option value="Pack">Pack</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Current Qty</label>
                              <input 
                                type="text" 
                                value={currentProductQuantity !== null ? currentProductQuantity : '0'}
                                readOnly
                                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg text-sm cursor-not-allowed"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Product Table - Fixed Header, Scrollable Body */}
                        <div className="bg-white rounded-xl border border-purple-200 shadow-sm overflow-hidden flex flex-col h-64 lg:h-80">
                          {/* Table Header - Fixed */}
                          <div className="bg-gray-800 text-white px-4 py-3 grid grid-cols-12 gap-2 font-medium text-sm flex-shrink-0">
                            <div className="col-span-4">PRODUCT</div>
                            <div className="col-span-1 text-right">QTY</div>
                            <div className="col-span-1 text-right">UOM</div>
                            <div className="col-span-2 text-right">PRICE</div>
                            <div className="col-span-2 text-right">AMOUNT</div>
                            <div className="col-span-2 text-right">ACTION</div>
                          </div>
                          
                                                     {/* Table Body - Scrollable */}
                           <div className="flex-1 overflow-y-auto bg-gray-50">
                            {selectedProducts.length === 0 ? (
                              <div className="flex items-center justify-center h-full">
                                <div className="text-center py-6">
                                  <div className="w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-r from-red-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Package size={24} className="lg:w-8 lg:h-8 text-white" />
                                  </div>
                                  <p className="text-gray-600 font-medium text-base lg:text-lg">{APP_NAME}</p>
                                  <p className="text-gray-500 text-sm mt-2">No products added yet</p>
                                  <p className="text-gray-400 text-xs mt-1">Search and add products to create your bulk order</p>
                                </div>
                              </div>
                            ) : (
                              <div className="divide-y divide-gray-200">
                                {selectedProducts.map((product) => (
                                  <div key={product.id} className="px-4 py-3 grid grid-cols-12 gap-2 items-center hover:bg-gray-100">
                                    <div className="col-span-4">
                                      <span className="font-medium text-gray-900 text-sm block truncate" title={product.name}>{product.name}</span>
                                    </div>
                                    <div className="col-span-1 text-sm font-medium text-gray-900 text-right">
                                      {product.quantity}
                                    </div>
                                    <div className="col-span-1 text-sm font-medium text-gray-900 text-right">
                                      {product.uom}
                                    </div>
                                    <div className="col-span-2 text-sm font-medium text-gray-900 text-right">
                                      {formatCurrency(product.price)}
                                    </div>
                                    <div className="col-span-2 text-sm font-medium text-gray-900 text-right">
                                      {formatCurrency(product.amount)}
                                    </div>
                                    <div className="col-span-2 flex items-center justify-end space-x-2">
                                      <button
                                        onClick={() => decreaseQuantity(product.id, product.uom)}
                                        disabled={product.quantity <= 1}
                                        className="w-6 h-6 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded flex items-center justify-center text-xs font-bold transition-colors"
                                        title="Decrease quantity"
                                      >
                                        -
                      </button>
                                      <button
                                        onClick={() => increaseQuantity(product.id, product.uom)}
                                        className="w-6 h-6 bg-green-500 hover:bg-green-600 text-white rounded flex items-center justify-center text-xs font-bold transition-colors"
                                        title="Increase quantity"
                                      >
                                        +
                                      </button>
                                      <button
                                        onClick={() => showDeleteConfirmation(product)}
                                        className="w-6 h-6 bg-red-600 hover:bg-red-700 text-white rounded flex items-center justify-center text-xs transition-colors"
                                        title="Delete item"
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                      </button>
                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                                              {/* Summary Section - Fixed */}
                                                <div className="bg-gray-800 text-white rounded-xl p-2 sm:p-3 lg:p-4 flex-shrink-0">
                          <div className="flex flex-col lg:flex-row justify-end space-y-2 sm:space-y-4 lg:space-y-0 lg:space-x-8 text-sm sm:text-base lg:text-lg">
                            <div className="flex items-center space-x-2 sm:space-x-4">
                              <span className="text-gray-300">Discount:</span>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-300">₵</span>
                                <input
                                  type="number"
                                  value={discount}
                                  onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                                  className="w-20 sm:w-24 bg-gray-700 text-white border border-gray-600 rounded px-6 py-1 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                  placeholder="0.00"
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                            </div>
                            <div className="flex items-center justify-between space-x-6">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-300">Items:</span>
                                <span className="text-lg font-bold text-white">{selectedProducts.length}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-300">Total Quantity:</span>
                                <span className="text-lg font-bold text-white">
                                  {selectedProducts.reduce((sum, product) => sum + (product.quantity || 0), 0)}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-gray-300">Subtotal:</span>
                                <span className="font-semibold">{formatCurrency(subtotal)}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-gray-300">VAT (15%):</span>
                                <span className="font-semibold">{formatCurrency(vat)}</span>
                              </div>
                              {discount > 0 && (
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-300">Discount:</span>
                                  <span className="font-semibold text-red-400">-{formatCurrency(discount)}</span>
                                </div>
                              )}
                              <div className="flex items-center space-x-2">
                                <span className="text-gray-300">Grand Total:</span>
                                <span className="font-bold text-green-400 text-xl">{formatCurrency(grandTotal)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                    </div>
                  </div>
                )}



                {activeModal === 'order-management' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Order Status</label>
                        <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                          <option>All Orders</option>
                          <option>Pending</option>
                          <option>Processing</option>
                          <option>Completed</option>
                          <option>Cancelled</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Order Number</label>
                        <input type="text" placeholder="Enter order number..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-medium text-gray-900 mb-3">Order Details</h3>
                      <div className="bg-white rounded-lg p-4 border">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="font-medium">Order #BO001</span>
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Completed</span>
                          </div>
                          <div className="text-sm text-gray-600">
                            <p>Customer: Wholesale Customer A</p>
                            <p>Total: ₵2,500.00</p>
                            <p>Items: 15 products</p>
                            <p>Date: 2024-01-15</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                        Update Status
                      </button>
                      <button onClick={handleModalClose} className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all duration-300">
                        Close
                      </button>
                    </div>
                  </div>
                )}

                {activeModal === 'bulk-reports' && (
                  <div className="flex flex-col h-full">
                    {/* Fixed Header Section */}
                    <div className="flex-shrink-0 space-y-6">
                      {/* Date Range Filter */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex flex-col sm:flex-row gap-4 items-end">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                            <input 
                              type="date" 
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              value={reportsDateRange.startDate}
                              onChange={(e) => handleReportsDateChange('startDate', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                            <input 
                              type="date" 
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                              value={reportsDateRange.endDate}
                              onChange={(e) => handleReportsDateChange('endDate', e.target.value)}
                            />
                          </div>
                          <div className="flex gap-2">
                            <button 
                              onClick={handleApplyReportsFilter}
                              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
                            >
                              Apply Filter
                            </button>
                            <button 
                              onClick={handleTodayReportsFilter}
                              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                            >
                              Today
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Report Tabs */}
                      <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                          <button
                            onClick={() => setActiveReportTab('dashboard')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                              activeReportTab === 'dashboard'
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            Dashboard
                          </button>
                          <button
                            onClick={() => setActiveReportTab('sales')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                              activeReportTab === 'sales'
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            Sales Report
                          </button>
                          <button
                            onClick={() => setActiveReportTab('products')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                              activeReportTab === 'products'
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            Products
                          </button>
                          <button
                            onClick={() => setActiveReportTab('customers')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                              activeReportTab === 'customers'
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            Customers
                          </button>
                        </nav>
                      </div>
                    </div>

                    {/* Scrollable Content Section */}
                    <div className="flex-1 overflow-y-auto p-6">
                      {/* Loading State */}
                      {reportsLoading && (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                          <span className="ml-2 text-gray-600">Loading reports...</span>
                        </div>
                      )}

                      {/* Dashboard Tab */}
                      {activeReportTab === 'dashboard' && !reportsLoading && reportsData.dashboardStats && (
                  <div className="space-y-6">
                          {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                              <h3 className="font-medium text-emerald-900">Total Bulk Orders</h3>
                              <p className="text-2xl font-bold text-emerald-600">
                                {reportsData.dashboardStats.total_orders || 0}
                              </p>
                              <p className="text-sm text-emerald-600">
                                {reportsData.dashboardStats.total_transactions || 0} transactions
                              </p>
                      </div>
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                              <h3 className="font-medium text-blue-900">Total Revenue</h3>
                              <p className="text-2xl font-bold text-blue-600">
                                {formatCurrency(reportsData.dashboardStats.total_revenue || 0)}
                              </p>
                              <p className="text-sm text-blue-600">
                                {reportsData.dashboardStats.total_transactions || 0} orders
                              </p>
                      </div>
                      <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                        <h3 className="font-medium text-orange-900">Average Order</h3>
                              <p className="text-2xl font-bold text-orange-600">
                                {formatCurrency(reportsData.dashboardStats.average_order || 0)}
                              </p>
                              <p className="text-sm text-orange-600">
                                Per order value
                              </p>
                      </div>
                    </div>

                          {/* Top Customers */}
                          <div className="bg-white border border-gray-200 rounded-lg">
                            <div className="px-4 py-3 border-b border-gray-200">
                              <h3 className="font-medium text-gray-900">Top Customers</h3>
                        </div>
                            <div className="divide-y divide-gray-200">
                              {reportsData.dashboardStats.top_customers?.map((customer, index) => (
                                <div key={index} className="px-4 py-3">
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {customer.customer_name}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        {customer.order_count} orders
                                      </p>
                        </div>
                                    <div className="text-right">
                                      <p className="text-sm font-medium text-gray-900">
                                        {formatCurrency(customer.total_amount)}
                                      </p>
                                      <p className="text-sm text-gray-500">Total Revenue</p>
                      </div>
                    </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Sales Report Tab */}
                      {activeReportTab === 'sales' && !reportsLoading && reportsData.salesReport && (
                        <div className="space-y-6">
                          {/* Sales Summary */}
                          <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h3 className="font-medium text-gray-900 mb-3">Sales Summary</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="text-center">
                                <p className="text-2xl font-bold text-emerald-600">
                                  {formatCurrency(reportsData.salesReport.summary?.total_sales || 0)}
                                </p>
                                <p className="text-sm text-gray-600">Total Sales</p>
                              </div>
                              <div className="text-center">
                                <p className="text-2xl font-bold text-blue-600">
                                  {reportsData.salesReport.summary?.total_transactions || 0}
                                </p>
                                <p className="text-sm text-gray-600">Total Orders</p>
                              </div>
                              <div className="text-center">
                                <p className="text-2xl font-bold text-orange-600">
                                  {formatCurrency(reportsData.salesReport.summary?.average_sale || 0)}
                                </p>
                                <p className="text-sm text-gray-600">Average Sale</p>
                              </div>
                            </div>
                          </div>

                          {/* Sales Table */}
                          <div className="bg-white border border-gray-200 rounded-lg">
                            <div className="px-4 py-3 border-b border-gray-200">
                              <h3 className="font-medium text-gray-900">Recent Sales</h3>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {reportsData.salesReport.recent_sales?.map((sale, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {sale.bulk_sale_id}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {sale.customer_name}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(sale.created_at).toLocaleDateString()}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                        {formatCurrency(sale.total_amount)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Products Report Tab */}
                      {activeReportTab === 'products' && !reportsLoading && reportsData.productsReport && (
                        <div className="space-y-6">
                          <div className="bg-white border border-gray-200 rounded-lg">
                            <div className="px-4 py-3 border-b border-gray-200">
                              <h3 className="font-medium text-gray-900">Top Products by Revenue</h3>
                            </div>
                            <div className="divide-y divide-gray-200">
                              {reportsData.productsReport.top_products?.map((product, index) => (
                                <div key={index} className="px-4 py-3">
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {product.product_name}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        {product.order_count} orders
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm font-medium text-gray-900">
                                        {formatCurrency(product.total_amount)}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        Qty: {product.total_quantity}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Customers Report Tab */}
                      {activeReportTab === 'customers' && !reportsLoading && reportsData.customersReport && (
                        <div className="space-y-6">
                          <div className="bg-white border border-gray-200 rounded-lg">
                            <div className="px-4 py-3 border-b border-gray-200">
                              <h3 className="font-medium text-gray-900">Top Customers by Revenue</h3>
                            </div>
                            <div className="divide-y divide-gray-200">
                              {reportsData.customersReport.top_customers?.map((customer, index) => (
                                <div key={index} className="px-4 py-3">
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {customer.customer_name}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        {customer.order_count} orders
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm font-medium text-gray-900">
                                        {formatCurrency(customer.total_amount)}
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        Avg: {formatCurrency(customer.average_order)}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* No Data State */}
                      {!reportsLoading && !reportsData.dashboardStats && (
                        <div className="text-center py-8">
                          <p className="text-gray-500">No reports data available. Please select a date range and apply filters.</p>
                        </div>
                      )}
                    </div>

                    {/* Export Buttons */}
                    <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
                      <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                        Export PDF
                      </button>
                      <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                        Export Excel
                      </button>
                      <button onClick={handleModalClose} className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300">
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Bulk Sales History Modal */}
      {activeModal === 'bulk-sales-history' && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Sticky Background */}
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300"></div>
          
          {/* Modal Content */}
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative w-full max-w-7xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col min-h-[80vh] max-h-[95vh]">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-green-400 via-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                      <Clock size={20} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white tracking-tight">Bulk Sales History</h2>
                      <p className="text-white/80 text-sm">View and manage all bulk sales transactions</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <input 
                        type="text" 
                        placeholder="Search customer name..."
                        value={bulkSalesHistorySearchTerm || ''}
                        onChange={(e) => handleBulkSalesHistorySearch(e.target.value)}
                        className="w-64 border border-white/30 rounded-lg px-4 py-2 bg-white/20 backdrop-blur-sm text-white placeholder-white/70 focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-200"
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {bulkSalesHistoryLoading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white/70"></div>
                        ) : (
                          <svg className="w-4 h-4 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <button 
                      onClick={handleExportToExcel} 
                      className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 border border-white/20 hover:border-white/30"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm font-medium">Excel</span>
                    </button>
                    <button 
                      onClick={handleExportToPDF} 
                      className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 border border-white/20 hover:border-white/30"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm font-medium">PDF</span>
                    </button>
                    <button
                      onClick={handleModalClose}
                      className="bg-red-500/90 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="flex-1 flex flex-col overflow-hidden bg-gradient-to-br from-gray-50 to-blue-50/30">
                <div className="flex-1 flex flex-col">
                  
                  {/* Table Section */}
                  <div className="flex-1 overflow-hidden p-4">
                    <div className="h-full bg-white/90 backdrop-blur-sm rounded-2xl shadow-inner border border-gray-200/30 overflow-hidden transform translate-y-1">
                      <div className="overflow-x-auto overflow-y-auto h-full" style={{ minHeight: '400px', maxHeight: 'calc(100vh - 350px)' }}>
                        <table className="w-full text-sm">
                          <thead className="bg-gradient-to-r from-gray-800 to-gray-900 text-white font-semibold sticky top-0 z-10">
                            <tr>
                              <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">BulkSale ID</th>
                              <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Customer Name</th>
                              <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Email</th>
                              <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Phone Number</th>
                              <th className="px-6 py-4 text-left text-xs uppercase tracking-wider">Amount</th>
                              <th className="px-6 py-4 text-center text-xs uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {bulkSalesHistoryLoading ? (
                              <tr>
                                <td colSpan="6" className="text-center py-8">
                                  <div className="flex flex-col items-center justify-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600 mb-2"></div>
                                    <span className="text-gray-600 text-sm">Searching...</span>
                                  </div>
                                </td>
                              </tr>
                            ) : bulkSalesHistory.length === 0 ? (
                              <tr>
                                <td colSpan="6" className="text-center py-8">
                                  <div className="flex flex-col items-center justify-center">
                                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                    </div>
                                    <span className="text-gray-600 font-medium">No bulk sales found</span>
                                    <span className="text-gray-500 text-sm">Try adjusting your search criteria</span>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              bulkSalesHistory.map((sale, index) => (
                                <tr key={sale.id} className={`hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/50 transition-all duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center">
                                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                                      <span className="font-semibold text-gray-900">{sale.bulk_sale_id}</span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center">
                                      <div className="w-8 h-8 bg-gradient-to-r from-green-400 to-green-500 rounded-full flex items-center justify-center mr-3">
                                        <span className="text-white text-xs font-bold">
                                          {(sale.customer_name || 'N/A').charAt(0).toUpperCase()}
                                        </span>
                                      </div>
                                      <span className="font-medium text-gray-900">{sale.customer_name || 'N/A'}</span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="text-gray-700">{sale.customer_email || 'N/A'}</span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="text-gray-700">{sale.customer_phone || 'N/A'}</span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-200">
                                      {formatCurrency(sale.total_amount)}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-center">
                                    <div className="flex items-center justify-center space-x-2">
                                      <button 
                                        onClick={() => handlePrintExistingReceipt(sale)}
                                        className="w-9 h-9 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md"
                                        title="Print Receipt"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                        </svg>
                                      </button>
                                      <button 
                                        onClick={() => handleEditSale(sale)}
                                        className="w-9 h-9 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md"
                                        title="Edit Sale"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                      </button>
                                      <button 
                                        onClick={() => handleDeleteSale(sale)}
                                        className="w-9 h-9 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md"
                                        title="Delete Sale"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border-t border-blue-200/50 shadow-inner">
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      <span className="font-medium text-blue-800">Total Records:</span> {bulkSalesHistoryTotal} bulk sales
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium text-blue-800">Scroll</span> to view all records
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Edit Sale Modal - Using Exact Create Bulk Sales Structure */}
      {showEditModal && selectedSale && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Sticky Background */}
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300"></div>
          
          {/* Modal Content */}
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative w-full max-w-6xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col max-h-[95vh]">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <button 
                      onClick={handleEditSubmit}
                      disabled={editLoading}
                      className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                    >
                      <FileText size={16} />
                      <span>{editLoading ? 'Updating...' : 'Update'}</span>
                    </button>
                    <div className="bg-gray-800 px-4 py-2 rounded-lg font-mono text-sm text-white">
                      <span className="font-bold">{selectedSale.bulk_sale_id}</span>
                    </div>
                    </div>
                  <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Close
                  </button>
                  </div>
                </div>
              </div>

              {/* Modal Body - Exact same structure as create modal */}
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 flex flex-col">
                  {/* Fixed Content - Scrollable on small screens, fixed on large screens */}
                  <div className="p-2 sm:p-3 lg:p-4 space-y-3 sm:space-y-4 lg:space-y-6 flex-shrink-0 overflow-y-auto lg:overflow-visible">
                    {/* Information Sections */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                      {/* Company Information */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-2 sm:p-3 lg:p-4 border border-blue-200">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center space-x-2">
                          <div className="w-5 h-5 bg-blue-500 rounded-lg flex items-center justify-center">
                            <Package size={14} className="text-white" />
                          </div>
                          <span>Company's Information</span>
                        </h3>
                                                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                           <div>
                             <label className="block text-xs font-medium text-gray-700 mb-1">Company (Store) Name</label>
                             <input 
                               type="text" 
                               value={editFormData.company_name}
                               onChange={(e) => setEditFormData(prev => ({ ...prev, company_name: e.target.value }))}
                               className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                             />
                           </div>
                           <div>
                             <label className="block text-xs font-medium text-gray-700 mb-1">Sales by / Agent</label>
                             <input 
                               type="text" 
                               value={user?.name || user?.username || user?.email || 'System User'}
                               readOnly
                               className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                             />
                           </div>
                           <div>
                             <label className="block text-xs font-medium text-gray-700 mb-1">Sales Date</label>
                             <div className="relative">
                               <input 
                                 type="text" 
                                 value={editFormData.sales_date}
                                 onChange={(e) => setEditFormData(prev => ({ ...prev, sales_date: e.target.value }))}
                                 className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8 text-sm"
                               />
                               <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                             </div>
                           </div>
                           <div>
                             <label className="block text-xs font-medium text-gray-700 mb-1">Mode of Sales</label>
                             <select 
                               value={editFormData.mode_of_sales}
                               onChange={(e) => setEditFormData(prev => ({ ...prev, mode_of_sales: e.target.value }))}
                               className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                             >
                               <option value="Cash">Cash</option>
                               <option value="Credit">Credit</option>
                               <option value="Bank Transfer">Bank Transfer</option>
                               <option value="Mobile Money">Mobile Money</option>
                             </select>
                           </div>
                           <div className="sm:col-span-2">
                             <label className="block text-xs font-medium text-gray-700 mb-1">Remark</label>
                             <input 
                               type="text" 
                               value={editFormData.remark}
                               onChange={(e) => setEditFormData(prev => ({ ...prev, remark: e.target.value }))}
                               placeholder="Optional note..."
                               className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                             />
                           </div>
                         </div>
                      </div>

                      {/* Customer Information */}
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-2 sm:p-3 lg:p-4 border border-green-200">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center space-x-2">
                          <div className="w-5 h-5 bg-green-500 rounded-lg flex items-center justify-center">
                            <User size={14} className="text-white" />
                          </div>
                          <span>Customer's Information</span>
                        </h3>
                                                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                           <div>
                             <label className="block text-xs font-medium text-gray-700 mb-1">Customer's ID</label>
                             <input 
                               type="text" 
                               value={selectedSale.bulk_sale_id}
                               placeholder="Customer ID"
                               className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                               readOnly
                             />
                           </div>
                           <div>
                             <label className="block text-xs font-medium text-gray-700 mb-1">Customer Name</label>
                             <input 
                               type="text" 
                               value={editFormData.customer_name}
                               onChange={(e) => setEditFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                               placeholder="Customer name"
                               className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                             />
                           </div>
                           <div>
                             <label className="block text-xs font-medium text-gray-700 mb-1">Phone Number</label>
                             <input 
                               type="tel" 
                               value={editFormData.customer_phone}
                               onChange={(e) => setEditFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                               placeholder="Phone number"
                               className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                             />
                           </div>
                           <div>
                             <label className="block text-xs font-medium text-gray-700 mb-1">Email (optional)</label>
                             <input 
                               type="email" 
                               value={editFormData.customer_email}
                               onChange={(e) => setEditFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                               placeholder="Email address"
                               className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                             />
                           </div>
                           <div className="sm:col-span-2">
                             <label className="block text-xs font-medium text-gray-700 mb-1">Customer's Address</label>
                             <input 
                               type="text" 
                               value={editFormData.customer_address}
                               onChange={(e) => setEditFormData(prev => ({ ...prev, customer_address: e.target.value }))}
                               placeholder="Street, City, Region..."
                               className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                             />
                           </div>
                         </div>
                      </div>
                    </div>

                    {/* Product Search Section - EXACT same as create modal */}
                    <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl p-2 sm:p-3 lg:p-4 border border-purple-200">
                      {/* Search Bar */}
                      <div className="bg-white rounded-xl p-2 sm:p-3 lg:p-4 border border-purple-200 shadow-sm mb-3 sm:mb-4">
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 sm:gap-3 lg:gap-4 items-end">
                          <div className="md:col-span-3">
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                              Search Products
                              {selectedProduct && (
                                <span className="ml-2 text-xs text-blue-600 font-medium">
                                  → {workflowStep === 'quantity' ? 'Enter Quantity' : workflowStep === 'uom' ? 'Select UOM' : 'Ready'}
                                </span>
                              )}
                            </label>
                            <div className="relative product-search-container">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                              <input 
                                type="text" 
                                value={searchQuery}
                                onChange={handleSearchChange}
                                onKeyDown={handleKeyDown}
                                onFocus={handleSearchFocus}
                                placeholder="Search products by Name and ProductID..."
                                className="w-full pl-10 pr-4 py-2 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm uppercase"
                              />
                              <button
                                onClick={getAllProducts}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white text-xs font-medium bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded transition-colors"
                                title="Show all products"
                              >
                                All
                              </button>
                              {searching && (
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                </div>
                              )}
                              {/* Search Results Dropdown */}
                              {products.length > 0 && showProductDropdown && !isQuantityFocused && !isUomFocused && !selectedProduct && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto">
                                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600 font-medium">
                                    {searchQuery.trim() ? `Found ${products.length} products` : `Showing all ${products.length} products`}
                                  </div>
                                  {products.map((product, index) => (
                                    <div
                                      key={product.id}
                                      data-index={index}
                                      className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150 ${
                                        index === selectedIndex 
                                          ? (selectedIndex % 2 === 0 ? 'bg-red-100 border-red-300' : 'bg-blue-100 border-blue-300')
                                          : 'hover:bg-gray-50'
                                      }`}
                                      onClick={() => handleProductSelect(product)}
                                    >
                                      <div className="flex justify-between items-center">
                                        <div className="flex-1">
                                          <div className="font-medium text-gray-900 text-sm leading-tight">
                                            {product.name}
                                          </div>
                                        </div>
                                        <div className="flex items-center space-x-3 ml-4">
                                          <div className="text-blue-600 font-semibold text-sm">
                                            {formatCurrency(product.selling_price_ctn || 0)}
                                          </div>
                                          <div className="text-green-600 font-medium text-xs">
                                            {formatCurrency(product.selling_price_pcs || 0)}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">(Qty)</label>
                            <input 
                              id="quantity-input"
                              type="number" 
                              defaultValue="1"
                              min="0"
                              step="0.01"
                              onFocus={(e) => {
                                setIsQuantityFocused(true);
                                e.target.select(); // Highlight the text
                              }}
                              onBlur={() => setIsQuantityFocused(false)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  setWorkflowStep('uom');
                                  const uomSelect = document.getElementById('uom-select');
                                  if (uomSelect) {
                                    uomSelect.focus();
                                    uomSelect.style.border = '2px solid #3b82f6';
                                    uomSelect.style.backgroundColor = '#dbeafe';
                                    uomSelect.style.boxShadow = '0 0 8px rgba(59, 130, 246, 0.3)';
                                    
                                    setTimeout(() => {
                                      uomSelect.style.border = '';
                                      uomSelect.style.backgroundColor = '';
                                      uomSelect.style.boxShadow = '';
                                    }, 2000);
                                  }
                                }
                              }}
                              className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">(UoM)</label>
                            <select 
                              id="uom-select"
                              tabIndex="0"
                              onFocus={(e) => {
                                e.target.focus();
                                setIsUomFocused(true);
                                e.target.style.border = '2px solid #3b82f6';
                                e.target.style.backgroundColor = '#dbeafe';
                              }}
                              onBlur={(e) => {
                                setIsUomFocused(false);
                                e.target.style.border = '';
                                e.target.style.backgroundColor = '';
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  if (selectedProduct && workflowStep === 'uom') {
                                    addToCart();
                                  }
                                }
                                if (e.key === ' ') {
                                  e.preventDefault();
                                }
                              }}
                              className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                            >
                              <option value="Ctn">Ctn</option>
                              <option value="Pcs">Pcs</option>
                              <option value="Kg">Kg</option>
                              <option value="Ltr">Ltr</option>
                              <option value="Box">Box</option>
                              <option value="Pack">Pack</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Current Qty</label>
                            <input 
                              type="text" 
                              value={currentProductQuantity !== null ? currentProductQuantity : '0'}
                              readOnly
                              className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-800 text-white border border-gray-700 rounded-lg text-sm cursor-not-allowed"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Product Table - Fixed Header, Scrollable Body */}
                      <div className="bg-white rounded-xl border border-purple-200 shadow-sm overflow-hidden flex flex-col h-64 lg:h-80">
                        {/* Table Header - Fixed */}
                        <div className="bg-gray-800 text-white px-4 py-3 grid grid-cols-12 gap-2 font-medium text-sm flex-shrink-0">
                          <div className="col-span-4">PRODUCT</div>
                          <div className="col-span-1 text-right">QTY</div>
                          <div className="col-span-1 text-right">UOM</div>
                          <div className="col-span-2 text-right">PRICE</div>
                          <div className="col-span-2 text-right">AMOUNT</div>
                          <div className="col-span-2 text-right">ACTION</div>
                        </div>
                        
                        {/* Table Body - Scrollable */}
                        <div className="flex-1 overflow-y-auto bg-gray-50">
                          {selectedProducts.length === 0 ? (
                            <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                              No products added to cart yet
                            </div>
                          ) : (
                            selectedProducts.map((product, index) => (
                              <div key={`${product.id}-${index}`} className="px-4 py-3 grid grid-cols-12 gap-2 items-center border-b border-gray-200 last:border-b-0 hover:bg-gray-100 transition-colors">
                                <div className="col-span-4">
                                  <div className="text-sm font-medium text-gray-900 lg:whitespace-normal sm:truncate">
                                    {product.name}
                                  </div>
                                </div>
                                <div className="col-span-1 text-right text-sm text-gray-900">
                                  {product.quantity}
                                </div>
                                <div className="col-span-1 text-right text-sm text-gray-900">
                                  {product.uom}
                                </div>
                                <div className="col-span-2 text-right text-sm text-gray-900">
                                  {formatCurrency(product.price)}
                                </div>
                                <div className="col-span-2 text-right text-sm font-medium text-gray-900">
                                  {formatCurrency(product.amount)}
                                </div>
                                <div className="col-span-2 flex items-center justify-end space-x-2">
                                  <button
                                    onClick={() => decreaseQuantity(product.id, product.uom)}
                                    disabled={product.quantity <= 1}
                                    className="w-6 h-6 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded flex items-center justify-center text-xs font-bold transition-colors"
                                    title="Decrease quantity"
                                  >
                                    -
                                  </button>
                                  <button
                                    onClick={() => increaseQuantity(product.id, product.uom)}
                                    className="w-6 h-6 bg-green-500 hover:bg-green-600 text-white rounded flex items-center justify-center text-xs font-bold transition-colors"
                                    title="Increase quantity"
                                  >
                                    +
                                  </button>
                                  <button
                                    onClick={() => showDeleteConfirmation(product)}
                                    className="w-6 h-6 bg-red-600 hover:bg-red-700 text-white rounded flex items-center justify-center text-xs transition-colors"
                                    title="Delete item"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Summary Section */}
                    <div className="bg-gradient-to-r from-slate-700 to-slate-800 rounded-xl p-4 border border-slate-600">
                      <div className="flex items-center justify-between space-x-6">
                        {/* Discount */}
                        <div className="flex items-center space-x-3">
                          <span className="text-gray-300 text-sm">Discount:</span>
                             <div className="flex items-center space-x-2">
                               <input
                                 type="number"
                                 value={editFormData.discount}
                                 onChange={(e) => setEditFormData(prev => ({ ...prev, discount: parseFloat(e.target.value) || 0 }))}
                              className="w-20 px-3 py-2 bg-slate-600 text-white border border-slate-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm text-right"
                              placeholder="0"
                                 min="0"
                                 step="0.01"
                               />
                            <span className="text-gray-300 text-sm">₵</span>
                             </div>
                           </div>
                           
                        {/* Subtotal */}
                        <div className="flex items-center space-x-3">
                          <span className="text-gray-300 text-sm">Subtotal:</span>
                          <span className="text-white font-bold">
                            ₵{selectedProducts.reduce((sum, product) => sum + parseFloat(product.amount || 0), 0).toFixed(2)}
                               </span>
                             </div>

                        {/* VAT */}
                        <div className="flex items-center space-x-3">
                          <span className="text-gray-300 text-sm">VAT (15%):</span>
                          <span className="text-white font-bold">
                            ₵{(selectedProducts.reduce((sum, product) => sum + parseFloat(product.amount || 0), 0) * 0.15).toFixed(2)}
                          </span>
                           </div>

                        {/* Grand Total */}
                        <div className="flex items-center space-x-3">
                          <span className="text-gray-300 text-sm">Grand Total:</span>
                          <span className="text-green-400 font-bold text-lg">
                            ₵{((selectedProducts.reduce((sum, product) => sum + parseFloat(product.amount || 0), 0) * 1.15) - (parseFloat(editFormData.discount) || 0)).toFixed(2)}
                          </span>
                         </div>
                        
                        {/* Update Button */}
                          <button
                            onClick={handleEditSubmit}
                            disabled={editLoading}
                          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                          >
                            {editLoading ? (
                              <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                              <span>Updating...</span>
                              </>
                            ) : (
                              <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span>Update Sale</span>
                              </>
                            )}
                          </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showBulkSaleDeleteModal && selectedSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Delete Bulk Sale</h2>
                    <p className="text-white/80 text-sm">{selectedSale.bulk_sale_id}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowBulkSaleDeleteModal(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="space-y-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Are you sure?</h3>
                  <p className="text-gray-600">
                    You are about to delete bulk sale <span className="font-semibold">{selectedSale.bulk_sale_id}</span>.
                    This action cannot be undone.
                  </p>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      // Remove the sale from the list
                      setBulkSalesHistory(prev => prev.filter(s => s.id !== selectedSale.id));
                      setBulkSalesHistoryTotal(prev => prev - 1);
                      setShowBulkSaleDeleteModal(false);
                      setSelectedSale(null);
                      
                      toast.success(`Bulk sale ${selectedSale.bulk_sale_id} deleted successfully`, {
                        style: {
                          background: '#1f2937',
                          color: '#fff',
                          border: '1px solid #374151'
                        }
                      });
                    }}
                    className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setShowBulkSaleDeleteModal(false)}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium transition-all duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && productToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Item</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700">
                Are you sure you want to delete <span className="font-semibold">{productToDelete.name}</span> from the cart?
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setProductToDelete(null);
                }}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => removeProduct(productToDelete.id, productToDelete.uom)}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && receiptData && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Background */}
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"></div>
          
          {/* Modal Content */}
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden border border-gray-200">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 px-8 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Bulk Sale Receipt</h2>
                      <p className="text-white/90 text-sm font-medium">Transaction ID: {receiptData.bulkSaleId}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handlePrintReceipt}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      <span>Print</span>
                    </button>
                    <button
                      onClick={handleCloseReceipt}
                      className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-8 max-h-[calc(95vh-140px)] overflow-y-auto">
                <div className="space-y-8">
                  {/* Header Section */}
                  <div className="space-y-6">
                    {/* Receipt Header */}
                    <div className="text-center">
                      <h1 className="text-3xl font-bold text-gray-800 mb-2">{APP_NAME}</h1>
                      <p className="text-gray-600 font-medium">Bulk Sale Receipt</p>
                      <div className="flex justify-between items-center text-sm text-gray-600 mt-4">
                        <span><strong>Receipt ID:</strong> {receiptData.bulkSaleId}</span>
                        <span><strong>Date:</strong> {receiptData.date}</span>
                        <span><strong>Time:</strong> {receiptData.time}</span>
                        <span><strong>Sales by:</strong> {user?.name || user?.username || 'Agent'}</span>
                      </div>
                    </div>

                    {/* Bill To and Bill From Section */}
                    <div className="flex gap-6">
                      {/* Bill To Section (Left) */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <h3 className="text-xl font-bold text-gray-800">Bill To</h3>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 space-y-3">
                          <div className="font-bold text-gray-900 text-lg">{receiptData.customer.name}</div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span>{receiptData.customer.phone}</span>
                          </div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span>{receiptData.customer.email}</span>
                          </div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>{receiptData.customer.address}</span>
                          </div>
                        </div>
                      </div>

                      {/* Bill From Section (Right) */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          </div>
                          <h3 className="text-xl font-bold text-gray-800">Bill From</h3>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 space-y-3">
                          <div className="font-bold text-gray-900 text-lg">{companySettings.company_name}</div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>{companySettings.company_address}</span>
                          </div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span>{companySettings.company_phone}</span>
                          </div>
                          {companySettings.tax_number && (
                            <div className="text-gray-600 flex items-center space-x-2">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                              </svg>
                              <span>Tax ID: {companySettings.tax_number}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Items Table */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <span>Items Purchased</span>
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gradient-to-r from-gray-100 to-gray-50">
                            <th className="border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-700 rounded-l-lg">Product</th>
                            <th className="border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700">Qty</th>
                            <th className="border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700">UoM</th>
                            <th className="border border-gray-200 px-4 py-3 text-right text-sm font-semibold text-gray-700">Unit Price</th>
                            <th className="border border-gray-200 px-4 py-3 text-right text-sm font-semibold text-gray-700 rounded-r-lg">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {receiptData.items.map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                              <td className="border border-gray-200 px-4 py-3 text-sm text-gray-800 font-medium">{item.name}</td>
                              <td className="border border-gray-200 px-4 py-3 text-center text-sm text-gray-800">{item.quantity}</td>
                              <td className="border border-gray-200 px-4 py-3 text-center text-sm text-gray-800">{item.uom}</td>
                              <td className="border border-gray-200 px-4 py-3 text-right text-sm text-gray-800">{formatCurrency(item.price)}</td>
                              <td className="border border-gray-200 px-4 py-3 text-right text-sm text-gray-800 font-semibold">{formatCurrency(item.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center space-x-2">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span>Payment Summary</span>
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 font-medium">Subtotal:</span>
                        <span className="font-semibold text-gray-800 text-lg">{formatCurrency(receiptData.subtotal)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 font-medium">VAT (15%):</span>
                        <span className="font-semibold text-gray-800 text-lg">{formatCurrency(receiptData.vat)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 font-medium">Discount:</span>
                        <span className="font-semibold text-red-600 text-lg">-{formatCurrency(receiptData.discount)}</span>
                      </div>
                      <div className="border-t-2 border-gray-300 pt-4 mt-4">
                        <div className="flex justify-between items-center">
                          <span className="text-xl font-bold text-gray-800">Grand Total:</span>
                          <span className="text-2xl font-bold text-green-600">{formatCurrency(receiptData.grandTotal)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-8 py-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    Receipt generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default BulkSales;